import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ApiHelperService } from '../../core/api-helper.service';
import { ErrorContext, ServerError } from '../errors/error.model';
import { OrganizationService } from '../organization/organization.service';
import { Team } from './team.model';

class TeamFetchError extends ServerError {
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);
        this.ctx.userMessage = 'We could not fetch the teams for this organization. Support has been notified.';
    }
}

@Injectable({
    providedIn: 'root'
})
export class TeamService {

    constructor(
        private orgService: OrganizationService,
        private api: ApiHelperService,
    ) { }

    options = { authenticate: true, setRoot: true };

    createTeam(team: Partial<Team>): Promise<Team> {
        const headers: HttpHeaders = this.api.getDefaultHeaders();
        return this.api.apiCallSane(
            '/team',
            'PUT',
            headers,
            null,
            team,
            this.options
        ).then((pt: Partial<Team>) => new Team(pt));
    }

    getTeamById(id: string): Promise<Team> {
        return this.api.apiCallSane(
            '/team',
            'GET',
            this.api.getDefaultHeaders(),
            { id },
            null,
            this.options
        ).then((pt: Partial<Team>) => new Team(pt));
    }

    updateTeam(id, data: Partial<Team>): Promise<Team> {
        return this.api.apiCallSane(
            '/team',
            'PATCH',
            this.api.getDefaultHeaders(),
            { id },
            data,
            this.options
        ).then((pt: Partial<Team>) => new Team(pt));
    }

    getTeams(filter: Partial<Team>): Promise<Team[]> {
        return this.api.apiCallSane(
            '/teams',
            'GET',
            this.api.getDefaultHeaders(),
            filter,
            null,
            this.options
        ).then((pt: Partial<Team>[]) => pt.map(p => new Team(p)));
    }

    getAllTeams(): Promise<Team[]> {
        return this.api.apiCallSane(
            '/teams',
            'GET',
            this.api.getDefaultHeaders(),
            null,
            null,
            this.options
        ).then((pts: Partial<Team>[]) => pts.map(pt => new Team(pt)))
        .catch(e => { throw TeamFetchError.upgrade(e) });
    }

    getGroupTeams(organizationId: string, groupId: string): Promise<Team[]> {
        return this.api.apiCallSane('/teams',
            'GET',
            this.api.getDefaultHeaders(),
            { organizationId, groupId },
            null,
            this.options
        ).then((pts: Partial<Team>[]) => pts.map(pt => new Team(pt)));
    }
}
